import outdent from "outdent";
import { oauthAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";

export const snowflake = defineConnector({
  identifier: "snowflake",
  name: "Snowflake",
  tagLine: "Cloud data platform",
  keywords: "data warehouse dwh olap analytics sql database",
  logo,
  protocol: "http",
  tags: ["featured", "data"],
  defaultUrlPattern: () => "https://[^/]*\\.snowflakecomputing\\.com/.*",
  auth: oauthAuth,
  steps: [
    {
      title: "Create OAuth security integration in Snowflake",
      items: [
        {
          kind: "markdown",
          content: outdent`
            Run the following SQL in your Snowflake instance to create a security integration.

            **Note:** You'll need to use an ACCOUNTADMIN role for this. [See Snowflake documentation](https://docs.snowflake.com/en/sql-reference/sql/create-security-integration).

            \`\`\`sql
            CREATE SECURITY INTEGRATION THREE_B
              TYPE = OAUTH
              ENABLED = TRUE
              OAUTH_CLIENT = CUSTOM
              OAUTH_CLIENT_TYPE = CONFIDENTIAL
              OAUTH_REDIRECT_URI = '{API_URL}/connected-apps/oauth/snowflake/callback'
              OAUTH_ISSUE_REFRESH_TOKENS = TRUE
              OAUTH_REFRESH_TOKEN_VALIDITY = 7776000;
            \`\`\`
          `,
        },
      ],
    },
    {
      title: "Retrieve credentials from Snowflake",
      items: [
        {
          kind: "markdown",
          content: outdent`
            After creating the security integration, run this SQL to get your credentials:

            \`\`\`sql
            SELECT
                parsed:OAUTH_CLIENT_ID::STRING as client_id,
                parsed:OAUTH_CLIENT_SECRET::STRING as client_secret,
                CONCAT('https://', (SELECT value:host::STRING FROM TABLE(FLATTEN(input => PARSE_JSON(SYSTEM$ALLOWLIST()))) LIMIT 1)) as url
            FROM (
                SELECT
                    PARSE_JSON(SYSTEM$SHOW_OAUTH_CLIENT_SECRETS('THREE_B')) as parsed
            ) subquery;
            \`\`\`
          `,
        },
      ],
    },
    {
      title: "Enter credentials to connect to 3B",
      items: [
        {
          kind: "input",
          label: "Client ID",
          required: true,
          target: { kind: "oauthConnection", field: "clientId" },
          placeholder: "Client ID from Snowflake",
        },
        {
          kind: "input",
          label: "Client secret",
          required: true,
          target: { kind: "oauthConnection", field: "clientSecret" },
          placeholder: "Client secret from Snowflake",
        },
        {
          kind: "url",
          label: "Snowflake URL",
          required: true,
          target: { kind: "configuration", field: "snowflakeUrl" },
          placeholder: "https://example.value.snowflakecomputing.com",
          helperText:
            "Your Snowflake account URL (e.g., https://example.snowflakecomputing.com)",
        },
        {
          kind: "input",
          label: "Access role",
          required: true,
          target: { kind: "configuration", field: "role" },
          placeholder: "ANALYST",
          helperText:
            "Role for 3B to use when accessing your data. To see all roles execute SHOW ROLES. To see any roles blocked for your integration, execute DESCRIBE SECURITY INTEGRATION THREE_B.",
        },
        {
          kind: "oauth",
          dependsOn: [
            { kind: "oauthConnection", field: "clientId" },
            { kind: "oauthConnection", field: "clientSecret" },
            { kind: "configuration", field: "snowflakeUrl" },
            { kind: "configuration", field: "role" },
          ],
        },
      ],
    },
  ],
  oauth: {
    grantType: () => "authorization_code",
    authorizationEndpoint: ({ snowflakeUrl }) =>
      `${snowflakeUrl}/oauth/authorize`,
    tokenEndpoint: ({ snowflakeUrl }) => `${snowflakeUrl}/oauth/token-request`,
    tokenEndpointAuthMethod: () => "client_secret_basic",
    clientCredentialsFrom: "db",
    scopes: ({ role }) => [`session:role:${role}`],
  },
  connectionTest: {
    protocol: "http",
    request: ({ snowflakeUrl }) => ({
      url: `${snowflakeUrl}/api/v2/statements`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ statement: "SELECT 1", timeout: 60 }),
    }),
  },
  publicEnvVars: ({ snowflakeUrl }) => ({
    SNOWFLAKE_URL: snowflakeUrl || "",
  }),
});
